/* ==========================================================================
   Aspire Leadership — motion primitives runtime
   Portable companion to motion.css. Names no section and no business.
   Drives: reveal · stagger · split · tilt · magnetic · counter · progress
           cursor · curtain · orbit placement · lightbox · accordion
   ========================================================================== */
(function () {
  'use strict';

  var root = document.documentElement;
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var hasGSAP = typeof window.gsap !== 'undefined';

  if (hasGSAP && window.ScrollTrigger) {
    window.gsap.registerPlugin(window.ScrollTrigger);
  }

  /* Tell the failsafe in <head> that we booted, so it stops counting down. */
  root.classList.add('js-motion-ready');

  /* ---------------------------------------------------------------- utils */
  function $(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  function rafThrottle(fn) {
    var queued = false, lastArgs;
    return function () {
      lastArgs = arguments;
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () { queued = false; fn.apply(null, lastArgs); });
    };
  }

  /* Shared observer factory — fires once, then releases the element. */
  function observeOnce(els, onEnter, opts) {
    if (!els.length) return;
    if (!('IntersectionObserver' in window) || reduced) {
      els.forEach(function (el) { onEnter(el); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        onEnter(entry.target);
        io.unobserve(entry.target);
      });
    }, opts || { threshold: 0.18, rootMargin: '0px 0px -8% 0px' });
    els.forEach(function (el) { io.observe(el); });
  }

  /* ------------------------------------------------------- 1 · reveal + stagger */
  function initReveals() {
    /* Assign stagger delays to sibling groups that didn't set one by hand. */
    $('[data-stagger]').forEach(function (group) {
      var step = parseInt(group.getAttribute('data-stagger'), 10) || 90;
      var cap = 640;
      $('[data-reveal]', group).forEach(function (el, i) {
        if (el.style.getPropertyValue('--reveal-delay')) return;
        el.style.setProperty('--reveal-delay', Math.min(i * step, cap) + 'ms');
      });
    });

    observeOnce($('[data-reveal], .draw-rule'), function (el) {
      el.classList.add('is-visible');
      /* Release will-change once the transition has run its course. */
      el.addEventListener('transitionend', function handler(e) {
        if (e.target !== el) return;
        el.style.willChange = 'auto';
        el.removeEventListener('transitionend', handler);
      });
    });
  }

  /* ------------------------------------------------------------ 2 · split text */
  /* Splits into lines, then into words or characters inside each line, so the
     mask (overflow:hidden per line) works and wrapping stays natural. */
  function splitHeading(el) {
    if (el.dataset.splitDone) return;
    if (el.children.length) return;          /* only plain-text headings */
    var mode = el.getAttribute('data-split') || 'word';
    var text = el.textContent.trim();
    var words = text.split(/(\s+)/);
    var frag = document.createDocumentFragment();
    var line = document.createElement('span');
    line.className = 'split-line';
    var i = 0;

    words.forEach(function (word) {
      if (/^\s+$/.test(word)) { line.appendChild(document.createTextNode(' ')); return; }
      /* Always wrap in a word box. Character spans are inline-block, so
         without this the browser is free to break *inside* a word — which
         it will, every time, at narrow widths. */
      var box = document.createElement('span');
      box.className = 'split-word';
      if (mode === 'char') {
        word.split('').forEach(function (ch) {
          var s = document.createElement('span');
          s.className = 'split-unit';
          s.style.setProperty('--unit-i', i++);
          s.textContent = ch;
          box.appendChild(s);
        });
      } else {
        var s = document.createElement('span');
        s.className = 'split-unit';
        s.style.setProperty('--unit-i', i++);
        s.textContent = word;
        box.appendChild(s);
      }
      line.appendChild(box);
      line.appendChild(document.createTextNode(' '));
    });

    frag.appendChild(line);
    el.textContent = '';
    el.appendChild(frag);
    el.dataset.splitDone = 'true';
    el.setAttribute('aria-label', text);   /* screen readers get the whole string */
  }

  function initSplits() {
    var els = $('[data-split]');
    if (reduced) { return; }               /* leave the text exactly as authored */
    els.forEach(splitHeading);
    observeOnce(els, function (el) { el.classList.add('is-split-in'); },
      { threshold: 0.25, rootMargin: '0px 0px -5% 0px' });
  }

  /* ------------------------------------------------------------- 3 · counters */
  function initCounters() {
    observeOnce($('[data-count-to]'), function (el) {
      var target = parseFloat(el.getAttribute('data-count-to'));
      var suffix = el.getAttribute('data-count-suffix') || '';
      var dur = 1400;
      if (reduced || isNaN(target)) { el.textContent = target + suffix; return; }
      var start = performance.now();
      (function tick(now) {
        var p = Math.min((now - start) / dur, 1);
        var eased = 1 - Math.pow(1 - p, 3);
        el.textContent = Math.round(target * eased) + suffix;
        if (p < 1) requestAnimationFrame(tick);
      })(start);
    });
  }

  /* ------------------------------------------------------- 4 · scroll progress */
  function initProgress() {
    var bar = document.querySelector('.scroll-progress');
    if (!bar) return;
    var update = rafThrottle(function () {
      var max = document.documentElement.scrollHeight - window.innerHeight;
      var p = max > 0 ? window.scrollY / max : 0;
      bar.style.transform = 'scaleX(' + p.toFixed(4) + ')';
    });
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    update();
  }

  /* ---------------------------------------------------------- 5 · sticky header */
  function initHeader() {
    var header = document.querySelector('[data-header]');
    if (!header) return;
    var update = rafThrottle(function () {
      header.classList.toggle('is-stuck', window.scrollY > 80);
    });
    window.addEventListener('scroll', update, { passive: true });
    update();

    var toggle = header.querySelector('[data-nav-toggle]');
    var panel = header.querySelector('[data-nav-panel]');
    if (!toggle || !panel) return;

    function setNav(open) {
      header.classList.toggle('is-nav-open', open);
      toggle.setAttribute('aria-expanded', String(open));
      /* Lock the page behind the overlay without losing scroll position. */
      document.body.style.overflow = open ? 'hidden' : '';
      if (open) {
        var first = panel.querySelector('a');
        if (first) first.focus({ preventScroll: true });
      } else {
        toggle.focus({ preventScroll: true });
      }
    }

    toggle.addEventListener('click', function () {
      setNav(!header.classList.contains('is-nav-open'));
    });

    /* Any link closes it — the overlay must never survive a navigation. */
    panel.addEventListener('click', function (e) {
      if (e.target.closest('a')) setNav(false);
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && header.classList.contains('is-nav-open')) setNav(false);
    });

    /* Crossing into the desktop breakpoint must not leave it stuck open. */
    var desktop = window.matchMedia('(min-width: 1000px)');
    (desktop.addEventListener ? desktop.addEventListener.bind(desktop, 'change')
                              : desktop.addListener.bind(desktop))(function (ev) {
      if ((ev.matches !== undefined ? ev.matches : desktop.matches)
          && header.classList.contains('is-nav-open')) {
        header.classList.remove('is-nav-open');
        toggle.setAttribute('aria-expanded', 'false');
        document.body.style.overflow = '';
      }
    });
  }

  /* --------------------------------------------------------------- 6 · cursor */
  function initCursor() {
    if (reduced) return;
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    var dot = document.createElement('div');
    var ring = document.createElement('div');
    dot.className = 'cursor-dot';
    ring.className = 'cursor-ring';
    document.body.appendChild(dot);
    document.body.appendChild(ring);

    var tx = window.innerWidth / 2, ty = window.innerHeight / 2;
    var rx = tx, ry = ty;
    var running = false;

    document.addEventListener('pointermove', function (e) {
      tx = e.clientX; ty = e.clientY;
      dot.style.transform = 'translate3d(' + tx + 'px,' + ty + 'px,0)';
      if (!running) { running = true; root.classList.add('has-cursor'); loop(); }
    }, { passive: true });

    document.addEventListener('pointerleave', function () {
      root.classList.remove('has-cursor');
    });

    function loop() {
      /* Lerp, never snap — the lag is what makes it feel alive. */
      rx += (tx - rx) * 0.14;
      ry += (ty - ry) * 0.14;
      ring.style.transform = 'translate3d(' + rx + 'px,' + ry + 'px,0)';
      requestAnimationFrame(loop);
    }

    var interactive = 'a, button, [role="button"], input, select, textarea, summary, .settle-card';
    document.addEventListener('pointerover', function (e) {
      if (e.target.closest(interactive)) root.classList.add('is-hovering-interactive');
    });
    document.addEventListener('pointerout', function (e) {
      if (e.target.closest(interactive)) root.classList.remove('is-hovering-interactive');
    });
  }

  /* ------------------------------------------------------------- 7 · magnetic */
  function initMagnetic() {
    if (reduced) return;
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    $('[data-magnetic]').forEach(function (el) {
      var strength = parseFloat(el.getAttribute('data-magnetic')) || 0.35;
      var radius = 60;

      function onMove(e) {
        var r = el.getBoundingClientRect();
        var cx = r.left + r.width / 2;
        var cy = r.top + r.height / 2;
        var dx = e.clientX - cx;
        var dy = e.clientY - cy;
        var dist = Math.hypot(dx, dy);
        if (dist > Math.max(r.width, r.height) / 2 + radius) { reset(); return; }
        el.classList.add('is-pulling');
        el.style.transform = 'translate3d(' + dx * strength + 'px,' + dy * strength + 'px,0)';
      }
      function reset() {
        el.classList.remove('is-pulling');
        el.style.transform = '';
      }
      window.addEventListener('pointermove', rafThrottle(onMove), { passive: true });
      el.addEventListener('pointerleave', reset);
      el.addEventListener('blur', reset);
    });
  }

  /* ----------------------------------------------------------------- 8 · tilt */
  function initTilt() {
    if (reduced) return;
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    $('[data-tilt]').forEach(function (el) {
      var max = parseFloat(el.getAttribute('data-tilt')) || 7;
      var move = rafThrottle(function (e) {
        var r = el.getBoundingClientRect();
        var px = (e.clientX - r.left) / r.width - 0.5;
        var py = (e.clientY - r.top) / r.height - 0.5;
        el.style.transform =
          'perspective(900px) rotateY(' + (px * max) + 'deg) rotateX(' + (-py * max) + 'deg) translate3d(0,-6px,0)';
        el.style.setProperty('--px', (px * 100 + 50).toFixed(1) + '%');
        el.style.setProperty('--py', (py * 100 + 50).toFixed(1) + '%');
      });
      el.addEventListener('pointermove', move);
      el.addEventListener('pointerleave', function () { el.style.transform = ''; });
    });
  }

  /* ---------------------------------------------------------------- 9 · orbit */
  /* Places nodes evenly around the ring and sizes the radius responsively. */
  function initOrbit() {
    $('[data-orbit]').forEach(function (orbit) {
      var nodes = $('.orbit__node', orbit);
      var n = nodes.length;
      nodes.forEach(function (node, i) {
        node.style.setProperty('--angle', (i * (360 / n) - 90) + 'deg');
      });
      function size() {
        var r = Math.min(orbit.offsetWidth, orbit.offsetHeight) / 2;
        orbit.style.setProperty('--orbit-r', (r * 0.78) + 'px');
      }
      size();
      window.addEventListener('resize', rafThrottle(size), { passive: true });
    });
  }

  /* -------------------------------------------------- 10 · horizontal pin (GSAP) */
  function initPinnedTrack() {
    var section = document.querySelector('[data-pin-track]');
    if (!section) return;
    var track = section.querySelector('[data-track]');
    if (!track) return;
    var cards = $('[data-track-card]', track);

    /* Reduced motion, no GSAP, or narrow viewport: fall back to a native
       horizontal scroller. The content and its order are identical. */
    function useFallback() {
      section.classList.add('is-swipe');
      cards.forEach(function (c) { c.classList.add('is-active'); });
    }
    if (reduced || !hasGSAP || !window.ScrollTrigger) { useFallback(); return; }

    var mm = window.gsap.matchMedia();
    mm.add('(min-width: 800px)', function () {
      var distance = track.scrollWidth - window.innerWidth + (window.innerWidth * 0.12);
      var tween = window.gsap.to(track, {
        x: -distance,
        ease: 'none',
        scrollTrigger: {
          trigger: section,
          start: 'top top',
          end: function () { return '+=' + distance; },
          pin: true,
          scrub: 0.8,
          invalidateOnRefresh: true,
          anticipatePin: 1
        }
      });

      /* Each card lifts as it reaches centre and dims as it leaves. */
      cards.forEach(function (card) {
        window.ScrollTrigger.create({
          trigger: card,
          containerAnimation: tween,
          start: 'left 62%',
          end: 'right 38%',
          onToggle: function (self) { card.classList.toggle('is-active', self.isActive); },
          onEnter: function () { typeIn(card); },
        });
      });
      return function () { mm.revert; };
    });

    mm.add('(max-width: 799px)', function () { useFallback(); });
  }

  /* Character-by-character type-in for the quote under a focused card. */
  function typeIn(card) {
    var el = card.querySelector('[data-typed]');
    if (!el || el.dataset.typedDone) return;
    el.dataset.typedDone = 'true';
    var text = el.getAttribute('data-typed');
    if (reduced) { el.textContent = text; return; }
    el.textContent = '';
    var i = 0;
    (function step() {
      el.textContent = text.slice(0, ++i);
      if (i < text.length) setTimeout(step, 22);
    })();
  }

  /* ----------------------------------------------------- 11 · hero choreography */
  function initHero() {
    var hero = document.querySelector('[data-hero]');
    if (!hero) return;

    var heading = hero.querySelector('[data-split]');
    if (heading) splitHeading(heading);

    /* Start the hero immediately — it plays under and through the curtain. */
    requestAnimationFrame(function () {
      hero.classList.add('is-in');
      if (heading) heading.classList.add('is-split-in');
    });

    if (reduced || !hasGSAP || !window.ScrollTrigger) return;

    /* Parallax: content rises at 0.4×, the WebGL layer holds still. */
    window.gsap.to(hero.querySelector('[data-hero-content]'), {
      yPercent: -18,
      ease: 'none',
      scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: true }
    });
  }

  /* --------------------------------------------------------------- 12 · curtain */
  function initCurtain() {
    var curtain = document.querySelector('.curtain');
    if (!curtain) return;
    if (reduced) { curtain.remove(); return; }

    function lift() {
      curtain.classList.add('is-lifting');
      setTimeout(function () { curtain.classList.add('is-done'); }, 1100);
    }
    /* Lift on load, but never hold the page hostage to a slow asset. */
    var lifted = false;
    function once() { if (!lifted) { lifted = true; lift(); } }
    window.addEventListener('load', function () { setTimeout(once, 420); });
    setTimeout(once, 2600);
  }

  /* -------------------------------------------------------------- 13 · lightbox */
  /* Scales open from the click origin. Focus is trapped and restored. */
  function initLightbox() {
    $('[data-lightbox]').forEach(function (trigger) {
      trigger.addEventListener('click', function (e) {
        e.preventDefault();
        var src = trigger.getAttribute('data-lightbox');
        var origin = trigger.getBoundingClientRect();
        var dlg = document.createElement('dialog');
        dlg.className = 'lightbox';
        dlg.innerHTML =
          '<button class="lightbox__close" aria-label="Close video">&times;</button>' +
          '<video class="lightbox__media" src="' + src + '" controls autoplay playsinline></video>';
        document.body.appendChild(dlg);

        var ox = origin.left + origin.width / 2 - window.innerWidth / 2;
        var oy = origin.top + origin.height / 2 - window.innerHeight / 2;
        dlg.style.setProperty('--origin-x', ox + 'px');
        dlg.style.setProperty('--origin-y', oy + 'px');

        dlg.showModal();
        requestAnimationFrame(function () { dlg.classList.add('is-open'); });

        function close() {
          dlg.classList.remove('is-open');
          setTimeout(function () { dlg.close(); dlg.remove(); trigger.focus(); }, 300);
        }
        dlg.querySelector('.lightbox__close').addEventListener('click', close);
        dlg.addEventListener('cancel', function (ev) { ev.preventDefault(); close(); });
        dlg.addEventListener('click', function (ev) { if (ev.target === dlg) close(); });
      });
    });
  }

  /* ------------------------------------------------------------- 14 · accordion */
  /* Smooth height without animating `height`: uses a grid-rows trick so the
     browser still lays the content out naturally. */
  function initAccordion() {
    $('[data-accordion] [data-acc-item]').forEach(function (item) {
      var btn = item.querySelector('[data-acc-trigger]');
      if (!btn) return;
      btn.addEventListener('click', function () {
        var open = item.classList.toggle('is-open');
        btn.setAttribute('aria-expanded', String(open));
        /* One open at a time within a group. */
        var group = item.closest('[data-accordion]');
        if (open && group) {
          $('[data-acc-item].is-open', group).forEach(function (other) {
            if (other === item) return;
            other.classList.remove('is-open');
            var ob = other.querySelector('[data-acc-trigger]');
            if (ob) ob.setAttribute('aria-expanded', 'false');
          });
        }
      });
    });
  }

  /* ------------------------------------------------------------ 15 · slider */
  function initSlider() {
    $('[data-slider]').forEach(function (slider) {
      var slides = $('[data-slide]', slider);
      if (slides.length < 2) { if (slides[0]) slides[0].classList.add('is-current'); return; }
      var dots = $('[data-slide-dot]', slider);
      var interval = parseInt(slider.getAttribute('data-slider'), 10) || 7000;
      var i = 0, timer = null;

      function go(next) {
        slides[i].classList.remove('is-current');
        if (dots[i]) dots[i].classList.remove('is-current');
        i = (next + slides.length) % slides.length;
        slides[i].classList.add('is-current');
        if (dots[i]) dots[i].classList.add('is-current');
      }
      function start() { if (!reduced) timer = setInterval(function () { go(i + 1); }, interval); }
      function stop() { clearInterval(timer); }

      go(0);
      start();
      slider.addEventListener('pointerenter', stop);
      slider.addEventListener('pointerleave', start);
      slider.addEventListener('focusin', stop);
      $('[data-slide-prev]', slider).forEach(function (b) {
        b.addEventListener('click', function () { stop(); go(i - 1); start(); });
      });
      $('[data-slide-next]', slider).forEach(function (b) {
        b.addEventListener('click', function () { stop(); go(i + 1); start(); });
      });
      dots.forEach(function (dot, n) {
        dot.addEventListener('click', function () { stop(); go(n); start(); });
      });
    });
  }

  /* --------------------------------------------------------------- 16 · Lottie */
  /* Renders inline JSON so nothing is fetched from a third party. */
  function initLottie() {
    if (typeof window.lottie === 'undefined') return;
    $('[data-lottie]').forEach(function (host) {
      var srcEl = document.getElementById(host.getAttribute('data-lottie'));
      if (!srcEl) return;
      var data;
      try { data = JSON.parse(srcEl.textContent); } catch (e) { return; }
      var anim = window.lottie.loadAnimation({
        container: host,
        renderer: 'svg',
        loop: true,
        autoplay: !reduced,
        animationData: data
      });
      if (reduced) anim.goToAndStop(Math.floor(anim.totalFrames / 2), true);
    });
  }

  /* --------------------------------------------------- 17 · the live layer */
  /* Everything that runs forever. Phases are spread deterministically so no
     two elements ever pulse in lockstep — synchronised loops are what make
     ambient motion read as cheap. */
  function initLive() {
    if (reduced) return;

    /* Floating brand quadrants inside every dark ambient section. */
    $('.ambient').forEach(function (section, si) {
      if (section.querySelector(':scope > .quads')) return;
      var layer = document.createElement('div');
      layer.className = 'quads';
      layer.setAttribute('aria-hidden', 'true');

      var count = section.offsetHeight > 900 ? 7 : 5;
      for (var i = 0; i < count; i++) {
        var seed = (si * 7 + i * 13) % 17;
        var q = document.createElement('span');
        q.className = 'quads__q';
        /* Scale with the section so a phone doesn't get desktop-sized blocks. */
        var basis = Math.min(section.offsetWidth, 900);
        var size = Math.round((0.05 + (seed % 5) * 0.028) * basis);
        var drift = 34;
        q.style.width = size + 'px';
        q.style.height = size + 'px';
        /* Keep the box plus its drift inside the section, so nothing can push
           the document wider than the viewport. */
        var maxLeftPx = Math.max(0, section.offsetWidth - size - drift);
        var leftPx = Math.min(((seed * 37) % 92) / 100 * section.offsetWidth, maxLeftPx);
        q.style.left = Math.round(leftPx) + 'px';
        q.style.top = (6 + ((seed * 53) % 78)) + '%';
        q.style.setProperty('--q-dur', (16 + (seed % 7) * 3) + 's');
        q.style.setProperty('--q-spin', (26 + (seed % 9) * 6) + 's');
        q.style.setProperty('--q-dx', (((seed % 5) - 2) * 17) + 'px');
        q.style.setProperty('--q-dy', (-30 - (seed % 6) * 16) + 'px');
        q.style.opacity = (0.28 + (seed % 4) * 0.13).toFixed(2);
        q.appendChild(document.createElement('i'));
        layer.appendChild(q);
      }
      section.insertBefore(layer, section.firstChild);
    });

    /* Spread the phase of every repeating loop. */
    function spread(sel, prop, step, unit) {
      $(sel).forEach(function (el, i) {
        if (el.style.getPropertyValue(prop)) return;
        el.style.setProperty(prop, (i * step).toFixed(2) + (unit || 's'));
      });
    }
    spread('.live-float', '--bob-delay', 0.65);
    spread('.live-sheen', '--sheen-delay', 1.15);
    spread('.live-pulse', '--pulse-delay', 0.32);
    spread('.orbit__label', '--node-delay', 0.44);
    spread('.live-media', '--sweep-delay', 1.7);

    /* Vary the Ken Burns duration and focal point per image. */
    $('.live-media').forEach(function (el, i) {
      el.style.setProperty('--kb-dur', (22 + (i % 5) * 4) + 's');
      el.style.setProperty('--kb-origin', (30 + (i % 4) * 15) + '% ' + (35 + (i % 3) * 18) + '%');
      el.style.setProperty('--sweep-dur', (7.5 + (i % 4) * 1.4) + 's');
      if (i % 2) el.classList.add('live-media--alt');
    });
    $('.live-float').forEach(function (el, i) {
      el.style.setProperty('--bob-dur', (5.2 + (i % 4) * 0.9) + 's');
    });

    /* Injected furniture for the loops that need their own overflow box. */
    $('.live-edge').forEach(function (el) {
      if (el.querySelector(':scope > .live-edge__bar')) return;
      var bar = document.createElement('span');
      bar.className = 'live-edge__bar';
      bar.setAttribute('aria-hidden', 'true');
      el.appendChild(bar);
    });
    $('.cta-band').forEach(function (el) {
      if (el.querySelector(':scope > .cta-band__streaks')) return;
      var s = document.createElement('span');
      s.className = 'cta-band__streaks';
      s.setAttribute('aria-hidden', 'true');
      el.insertBefore(s, el.firstChild);
    });
    $('[data-track-card]').forEach(function (el) {
      if (el.querySelector(':scope > .track-card__beam')) return;
      var s = document.createElement('span');
      s.className = 'track-card__beam';
      s.setAttribute('aria-hidden', 'true');
      el.appendChild(s);
    });

    /* ---- run only what is on screen -----------------------------------
       Without this, every loop on a 14,000px page runs at once and the
       frame rate collapses. `--play` inherits, so pausing the section
       pauses its pseudo-elements too. */
    var sections = $('section, .footer');
    sections.forEach(function (s) { s.setAttribute('data-live', ''); });

    if (!('IntersectionObserver' in window)) {
      sections.forEach(function (s) { s.classList.add('is-live'); });
      return;
    }
    var liveIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        e.target.classList.toggle('is-live', e.isIntersecting);
      });
    }, { rootMargin: '15% 0px 15% 0px' });
    sections.forEach(function (s) { liveIO.observe(s); });
  }

  /* ------------------------------------------------------------------- boot */
  function boot() {
    initReveals();
    initSplits();
    initCounters();
    initProgress();
    initHeader();
    initCursor();
    initMagnetic();
    initTilt();
    initOrbit();
    initHero();
    initCurtain();
    initPinnedTrack();
    initLightbox();
    initAccordion();
    initSlider();
    initLottie();
    initLive();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Shopify theme editor: re-run for sections added or reordered live. */
  document.addEventListener('shopify:section:load', function () {
    boot();
    if (hasGSAP && window.ScrollTrigger) window.ScrollTrigger.refresh();
  });
})();
